import { createContext, useEffect, useState } from "react";

// ─────────────────────────────────────────────────────────────
// 🏷️  PROMO: 5-Gallon Water Bottle Bulk Discount Tiers
// Only edit this table when the promotion changes
// ─────────────────────────────────────────────────────────────
export const DISCOUNT_TIERS = [
  { min: 1, max: 1, rate: 0 }, //  0% off
  { min: 2, max: 3, rate: 0.2 }, // 20% off
  { min: 4, max: 5, rate: 0.25 }, // 25% off
  { min: 6, max: 9, rate: 0.3 }, // 30% off
  { min: 10, max: 100, rate: 0.35 }, // 35% off
];

const MAX_BOTTLE_QTY = 100;

// Tag products eligible for bulk discount by their ID or a flag on the product
// ✅ Option A: use a product flag  → product.isBulkPromo === true
// ✅ Option B: hardcode IDs        → PROMO_PRODUCT_IDS.has(product.id)
// Using Option A below — set isBulkPromo: true on your 5-Gallon product in the API/DB
// 9000464	5 Gallon WTBT for Production, QA = 9004278
const PROMO_PRODUCT_IDS = new Set(["9000464", "9004278"]); // Production + QA IDs

const isPromoProduct = (item) => PROMO_PRODUCT_IDS.has(String(item.id));

export const getDiscountRate = (qty) => {
  const tier = DISCOUNT_TIERS.find((t) => qty >= t.min && qty <= t.max);
  return tier ? tier.rate : 0;
};

// Returns the discounted unit price for promo products, original CUSTOMER
// price otherwise. FIX: falls back to item.customerPrice (locked in once,
// at add-time, in addCartItem) instead of item.price -- item.price gets
// overwritten by the mosque-mode effect below, so using it as the "reset"
// fallback meant non-promo items stayed stuck at the mosque-discounted
// price after switching back to "delivery". customerPrice is never touched
// by mosque logic, so it's always the correct value to restore to.
const resolvePrice = (item, qty) => {
  if (!isPromoProduct(item)) return item.customerPrice ?? item.price;
  const rate = getDiscountRate(qty);
  const base = parseFloat(String(item.actualprice).replace(/[^\d.]/g, "")); // strips " AED"
  return parseFloat((base * (1 - rate)).toFixed(2));
};
// ─────────────────────────────────────────────────────────────

const addCartItem = (cartItems, productToAdd) => {
  const existingCartItem = cartItems.find(
    (item) => item.id === productToAdd.id
  );

  if (existingCartItem) {
    return cartItems.map((cartItem) => {
      if (cartItem.id !== productToAdd.id) return cartItem;

      const newQty = Math.min(cartItem.quantity + 1, MAX_BOTTLE_QTY); // cap at 10 for promo items
      productToAdd.quantity = newQty;

      return {
        ...cartItem,
        quantity: newQty,
        price: resolvePrice(cartItem, newQty), // recalculate discount (customerPrice already on cartItem from when it was first added)
        discountPercent: Math.round(getDiscountRate(newQty) * 100), // for badge
      };
    });
  }

  // First time added to cart
  productToAdd.quantity = 1;

  const newItem = {
    ...productToAdd,
    quantity: 1,
    actualprice: productToAdd.actualprice, // lock in base price
    customerPrice: productToAdd.price, // NEW -- lock in the true customer price at add-time. Mosque mode only ever overwrites `price`, never this field, so it's always safe to restore from.
    discountPercent: 0,
  };
  newItem.price = resolvePrice(newItem, 1); // no discount at qty 1 for non-promo items; tiered discount for promo items

  return [...cartItems, newItem];
};

const removeCartItem = (cartItems, productToRemove) => {
  const existingCartItem = cartItems.find(
    (item) => item.id === productToRemove.id
  );

  if (existingCartItem.quantity === 1) {
    productToRemove.quantity = 0;
    return cartItems.filter((item) => item.id !== productToRemove.id);
  }

  if (existingCartItem) {
    return cartItems.map((cartItem) => {
      if (cartItem.id !== productToRemove.id) return cartItem;

      const newQty = cartItem.quantity - 1;
      productToRemove.quantity = newQty;

      return {
        ...cartItem,
        quantity: newQty,
        price: resolvePrice(cartItem, newQty), // recalculate discount
        discountPercent: Math.round(getDiscountRate(newQty) * 100), // for badge
      };
    });
  }
};

const clearCartItem = (cartItems, productToRemove) => {
  productToRemove.quantity = 0;
  return cartItems.filter((item) => item.id !== productToRemove.id);
};

export const CartContext = createContext({
  isCartOpen: false,
  setIsCartOpen: () => {},
  cartItems: [],
  addItemToCart: () => {},
  removeItemToCart: () => {},
  clearItemfromCart: () => {},
  setCartItems: () => {},
  cartCount: 0,
  cartTotal: 0,
  shippingCharges: 0,
  cartGTotal: 0,
  deliveryOption: "delivery",
  setDeliveryOption: () => {},
  actualTotal: 0,
  cartDiscount: 0,
  mosquePrices: {},
  setMosquePrices: () => {},
  bottleDepositAmount: 0,
  setBottleDepositAmount: () => {},
  cartGrandTotal: 0,
});

export const CartProivder = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [actualTotal, setActualTotal] = useState(0);
  const [cartDiscount, setCartDiscount] = useState(0);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [cartGTotal, setCartGTotal] = useState(0);
  const [deliveryOption, setDeliveryOption] = useState("delivery");
  const [mosquePrices, setMosquePrices] = useState({});
  const [bottleDepositAmount, setBottleDepositAmount] = useState(0);

  // Cart count -- unchanged
  useEffect(() => {
    const newCartCount = cartItems.reduce(
      (acc, item) => acc + item.quantity,
      0
    );
    setCartCount(newCartCount);
  }, [cartItems]);

  // Cart totals -- unchanged, works automatically because item.price is already discounted
  useEffect(() => {
    const newCartTotal = cartItems.reduce(
      (acc, item) => acc + parseInt(item.quantity) * parseFloat(item.price),
      0
    );

    const mactualTotal = cartItems.reduce(
      (acc, item) =>
        acc + parseInt(item.quantity) * parseFloat(item.actualprice),
      0
    );

    setActualTotal(mactualTotal);

    const mcartDiscount = mactualTotal - newCartTotal;
    setCartDiscount(mcartDiscount.toFixed(2));

    const shCharges = 0;
    setShippingCharges(0);

    const vatAmount = (parseFloat(newCartTotal) * 0.05).toFixed(2);
    const gTotal =
      parseFloat(newCartTotal) + parseInt(shCharges) + parseFloat(vatAmount);

    setCartGTotal(gTotal);
    setCartTotal(newCartTotal);
  }, [cartItems]);

  // Mosque/delivery price switching.
  // Mosque branch only overwrites `price` -- `customerPrice` is left
  // untouched, which is exactly what makes the delivery-branch reset below
  // work correctly now.
  useEffect(() => {
    if (deliveryOption === "mosque") {
      setCartItems((cartItems) =>
        cartItems.map((item) => ({
          ...item,
          price: mosquePrices[item.id] || item.price,
        }))
      );
    } else if (deliveryOption === "delivery") {
      setCartItems((cartItems) =>
        cartItems.map((item) => ({
          ...item,
          // FIX: resolvePrice now falls back to item.customerPrice for
          // non-promo items instead of item.price, so this correctly
          // restores the real customer price instead of re-applying the
          // stale mosque price that was sitting in item.price.
          price: resolvePrice(item, item.quantity),
        }))
      );
    }
  }, [deliveryOption, mosquePrices]);

  const addItemToCart = (productToAdd) =>
    setCartItems(addCartItem(cartItems, productToAdd));
  const removeItemToCart = (productToRemove) =>
    setCartItems(removeCartItem(cartItems, productToRemove));
  const clearItemfromCart = (productToRemove) =>
    setCartItems(clearCartItem(cartItems, productToRemove));

  const cartGrandTotal = cartGTotal + bottleDepositAmount;

  const value = {
    isCartOpen,
    setIsCartOpen,
    addItemToCart,
    removeItemToCart,
    clearItemfromCart,
    setCartItems,
    cartItems,
    cartCount,
    cartTotal,
    shippingCharges,
    cartGTotal,
    actualTotal,
    cartDiscount,
    deliveryOption,
    setDeliveryOption,
    mosquePrices,
    setMosquePrices,
    bottleDepositAmount,
    setBottleDepositAmount,
    cartGrandTotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
